import {
  asNode,
  isContainer,
  isPlainObject,
  type Resolved,
  requiredList,
  resolve,
  type SchemaNode,
  titleOf,
} from './mcp-tool-json-schema'
import { humanizeKey } from './mcp-tool-labels'

export interface PickOneKey {
  path: string[]
  container: boolean
}

/** One oneOf / anyOf list of a container whose members each name the keys of an alternative. */
export interface PickOneGroup {
  path: string[]
  /** oneOf: exactly one alternative; anyOf: at least one. */
  exclusive: boolean
  /** Keys every alternative names: required whenever the group is sent. */
  shared: PickOneKey[]
  /** Each alternative's other keys. */
  alternatives: PickOneKey[][]
  /** The container's own `required`: agent-runner always sends those members. */
  requiredKeys: unknown[]
  /** The nearest optional container at or above this one; undefined when every call sends it. */
  activatedBy?: string[]
  /** The alternatives by label, e.g. "Database ID, Page ID". */
  choices: string
  /** Whether there is a choice to tell the builder about. */
  hinted: boolean
}

export const within = (path: string[], group: string[]) =>
  group.every((segment, index) => path[index] === segment)

export const pickOneMessage = ({ choices }: PickOneGroup) => `Fill one of: ${choices}`

const bareRequired = (member: unknown) => {
  const keys = asNode(member).required
  const bare = isPlainObject(member) && Object.keys(member).length === 1 && Array.isArray(keys)
  return bare && keys.every((key) => typeof key === 'string') ? (keys as string[]) : undefined
}

// Only a oneOf / anyOf of bare `{required: [...]}` members says "fill one of these"; a container
// may carry both lists, and each is its own constraint.
export const pickOnesOf = (
  root: SchemaNode,
  container: Resolved,
  path: string[],
  activatedBy: string[] | undefined,
): PickOneGroup[] => {
  const { schema } = container
  const properties = asNode(schema.properties)
  const memberOf = (key: string) => resolve(root, asNode(properties[key]), container.refs)
  const keyOf = (key: string) => ({ path: [...path, key], container: isContainer(memberOf(key)) })
  return [schema.oneOf, schema.anyOf].flatMap((list): PickOneGroup[] => {
    const choices = Array.isArray(list) ? list.map(bareRequired) : []
    if (!choices.length || choices.some((keys) => !keys)) return []
    const sets = choices as string[][]
    const shared = sets[0].filter((key) => sets.every((keys) => keys.includes(key)))
    const alternatives = sets.map((keys) => keys.filter((key) => !shared.includes(key)))
    const labelled = alternatives
      .filter((keys) => keys.length)
      .map((keys) =>
        keys.map((key) => titleOf(memberOf(key).schema) || humanizeKey(key)).join(' and '),
      )
    return [
      {
        path,
        exclusive: list === schema.oneOf,
        shared: shared.map(keyOf),
        alternatives: alternatives.map((keys) => keys.map(keyOf)),
        requiredKeys: requiredList(schema),
        activatedBy,
        choices: labelled.join(', '),
        hinted: labelled.length > 1,
      },
    ]
  })
}
